import React, { useEffect, useRef, useState } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const COLS = 12;
const ROW_HEIGHT = 40;
const MARGIN = [10, 10];

const CSS = `
  .dash-grid .react-grid-item.react-grid-placeholder {
    background: color-mix(in srgb, var(--brand-primary) 8%, transparent) !important;
    border: 1.5px dashed color-mix(in srgb, var(--brand-primary) 40%, transparent) !important;
    border-radius: 8px !important;
  }
  .dash-grid .react-grid-item > .react-resizable-handle { opacity: 0; transition: opacity .15s; }
  .dash-grid .react-grid-item:hover > .react-resizable-handle { opacity: .4; }
  .dash-grid .react-grid-item.react-draggable-dragging { z-index: 100 !important; }
`;

/** Below this width the saved layout is not drawn: 12 columns on a phone are 25px each. */
const NARROW = 640;

/**
 * The layout for a phone: the saved order (top to bottom, left to right),
 * number tiles two to a row, everything else full width. Never saved — the
 * grid is read-only at this width.
 */
function stacked(layout) {
  const out = [];
  let y = 0;
  let half = null;
  for (const l of [...layout].sort((a, b) => (a.y - b.y) || (a.x - b.x))) {
    if (l.w <= 3 && l.h <= 4) {
      if (half) { out.push({ ...l, x: 6, y: half.y, w: 6, static: true }); y = half.y + l.h; half = null; continue; }
      half = { y };
      out.push({ ...l, x: 0, y, w: 6, static: true });
      y += l.h;
      continue;
    }
    if (half) half = null;
    out.push({ ...l, x: 0, y, w: 12, h: Math.max(l.h, 6), static: true });
    y += Math.max(l.h, 6);
  }
  return out;
}

/**
 * The 12-column grid. GridLayout needs a pixel width, measured at mount and
 * on resize — the app replaces ResizeObserver globally and its first entry is
 * not reliable, so the width is read synchronously first.
 */
export default function DashboardGrid({ layout, editing, onLayoutChange, children }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return undefined;
    const measure = () => {
      const w = ref.current?.getBoundingClientRect().width;
      if (w && w > 0) setWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const narrow = width > 0 && width < NARROW;
  return (
    <div ref={ref} className="dash-grid w-full">
      <style>{CSS}</style>
      {width > 0 ? (
        <GridLayout
          layout={narrow ? stacked(layout) : layout}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          width={width}
          margin={MARGIN}
          containerPadding={[0, 0]}
          onLayoutChange={narrow ? () => {} : onLayoutChange}
          isDraggable={editing && !narrow}
          isResizable={editing && !narrow}
          compactType="vertical"
          preventCollision={false}
          draggableHandle=".dash-card-drag"
          resizeHandles={['se']}
          useCSSTransforms
        >
          {children}
        </GridLayout>
      ) : null}
    </div>
  );
}
